import { create } from "zustand"
import type { client, ClientOutput } from "./ts-rest"
import type { ClientInferResponseBody } from "@ts-rest/core"
import type { contractRouter } from '@simulead/api/contract'
import type { AnalyticsFilters } from "@simulead/shared"
import { persist } from "zustand/middleware"
import { DateTime } from "luxon"

type AuthData = ClientInferResponseBody<typeof contractRouter.getAuthData, 200>

// ENG-1214: pure UI preference for the pre-call camera mode picker.
// Intentionally NOT persisted on the User row server-side — no backend
// consumer reads it, and a localStorage-backed Zustand value avoids a
// round-trip on every settings toggle. Browser-scoped is acceptable;
// the user re-picks pre-call if they care.
export type CameraMode = "Off" | "SelfView" | "Record"

interface BearState {
  emulateUserId: number | undefined
  userId: number | undefined
  // The authenticated user's id captured at the moment emulation started, so
  // admin routes that bypass emulation (see queriesToSkipEmulationFor in
  // lib/ts-rest.ts) can still send a valid user-id header.
  userIdBeforeStartedEmulating: number | undefined
  cameraMode: CameraMode
  // True once the user has EXPLICITLY picked a camera mode anywhere (pre-call
  // sheet, presentation pre-flight, settings tiles). Lets surfaces with a
  // different historical default (presentations started camera-ON) keep that
  // default until the user expresses a real preference, instead of silently
  // inheriting the calls surface's "Off".
  cameraModeChosen: boolean
  // Session-setup option keys the user has been walked through on the
  // pre-call screen (see setupKeysFor in SessionSetupChips.tsx). LocalStorage-
  // scoped ON PURPOSE: a new browser or device re-runs the first-time
  // walkthrough, re-introducing the choices where defaults may differ. Also
  // re-opens the sheet once when a NEW option appears for an existing user
  // (e.g. the org enables session recording, adding "camera:record").
  setupIntroSeen: string[]
  // Per-user body-language opt-out (default ON for Labs enrollees — the
  // PostHog flag gates visibility, this remembers the user's own switch).
  // Persisted like cameraMode so the choice carries across sessions.
  bodyLanguageEnabled: boolean
  // Persistent filter bar state for the /reports section. Survives refresh
  // and is shared across every /reports/* route.
  reportingFilters: AnalyticsFilters
  // Last visited Analytics sub-tab, so the main admin nav returns the admin
  // to the tab they were on instead of always resetting to Overview.
  reportingLastPath: ReportingPath
  setPermStore: (data: Partial<Omit<BearState, "set">>) => void
}

// The four Analytics sub-tab paths (see adminReporting routes in router.ts).
export type ReportingPath =
  | "/admin"
  | "/admin/analytics/individuals"
  | "/admin/analytics/completion"
  | "/admin/analytics/journeys"

// ---------------------------------------------------------------------------
// Date-range presets for the reporting filters. These live here (not in the
// filter bar component) because the persisted range must be re-anchored at
// store REHYDRATION time — before anything renders — so a stale "Last 30
// days" window can neither display wrong numbers nor trigger work (like a
// Learning Insights generation) for a window that's about to be corrected.
// ---------------------------------------------------------------------------

export type DateRangePreset = "7d" | "30d" | "90d" | "all" | "custom"

export function rangeForPreset(
  preset: DateRangePreset
): AnalyticsFilters["dateRange"] {
  if (preset === "all") return undefined
  const now = DateTime.now().toUTC()
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90
  return { from: now.minus({ days }).toISO()!, to: now.toISO()! }
}

// Derive the chip label from the *persisted* range so a reload doesn't always
// fall back to "Last 30 days". Calendar-picked custom ranges snap `to` to the
// end of a day, while relative presets anchor `to` to "now" (an arbitrary
// time) — so an end-of-day `to` always means an explicit custom pick and is
// never reclassified as a rolling preset (which would both relabel it and
// re-anchor the user's deliberately chosen window away on the next visit).
// Otherwise a span of exactly 7/30/90 days reads as that preset; anything
// else is "custom".
export function presetForRange(
  range: AnalyticsFilters["dateRange"]
): DateRangePreset {
  if (!range) return "all"
  const from = DateTime.fromISO(range.from)
  const to = DateTime.fromISO(range.to)
  if (from.isValid && to.isValid) {
    if (to.toMillis() === to.endOf("day").toMillis()) return "custom"
    const days = Math.round(to.diff(from, "days").days)
    if (days === 7) return "7d"
    if (days === 30) return "30d"
    if (days === 90) return "90d"
  }
  return "custom"
}

// Relative presets ("Last 30 days") persist as absolute timestamps, which go
// stale: come back two weeks later and the window still ends two weeks ago
// while the chip claims "Last 30 days". Re-anchor the stored range to now
// whenever it reads as a relative preset and its end has drifted by more
// than an hour. Runs at rehydration (below) so stale windows never render.
export function reanchorRelativeDateRange(
  filters: AnalyticsFilters
): AnalyticsFilters {
  const range = filters.dateRange
  if (!range) return filters
  const preset = presetForRange(range)
  if (preset !== "7d" && preset !== "30d" && preset !== "90d") return filters
  const to = DateTime.fromISO(range.to)
  if (to.isValid && Math.abs(to.diffNow("hours").hours) > 1) {
    return { ...filters, dateRange: rangeForPreset(preset) }
  }
  return filters
}

// Filters saved before ENG-1138 hold numeric scenario and category IDs, which
// the analytics API now rejects with a 400. Convert them to strings at
// rehydration so those admins keep their selection and the page loads.
export function stringifyLegacyFilterIds(
  filters: AnalyticsFilters
): AnalyticsFilters {
  return {
    ...filters,
    ...(filters.scenarioIds && {
      scenarioIds: filters.scenarioIds.map(String),
    }),
    ...(filters.category && { category: filters.category.map(String) }),
  }
}

export const usePermStore = create<BearState>()(
  persist(
    (set) => ({
      emulateUserId: undefined,
      userId: undefined,
      userIdBeforeStartedEmulating: undefined,
      cameraMode: "Off",
      cameraModeChosen: false,
      setupIntroSeen: [],
      bodyLanguageEnabled: true,
      reportingFilters: {},
      reportingLastPath: "/admin",
      setPermStore: set,
    }),
    {
      name: "permStore",
      // Normalise at rehydration, before the first render consumes the store.
      merge: (persisted, current) => {
        const persistedPartial = persisted as Partial<BearState>
        const merged = {
          ...current,
          ...persistedPartial,
        }
        // Sanitise the camera fields — localStorage is user-editable and
        // survives version rollbacks, and downstream code branches on these
        // values without re-checking (a bogus cameraMode would open the
        // camera via `mode !== "Off"`; a non-array setupIntroSeen would
        // throw in render).
        const validCameraModes: CameraMode[] = ["Off", "SelfView", "Record"]
        if (!validCameraModes.includes(merged.cameraMode)) {
          merged.cameraMode = "Off"
        }
        if (
          !Array.isArray(merged.setupIntroSeen) ||
          merged.setupIntroSeen.some((key) => typeof key !== "string")
        ) {
          merged.setupIntroSeen = []
        }
        // Migration: payloads written before `cameraModeChosen` existed can't
        // say whether the mode was picked — but a persisted non-default mode
        // could ONLY have come from an explicit pick (the default is "Off"
        // and nothing else wrote the field), so treat it as chosen. This
        // keeps a pre-existing explicit "Record" driving presentation
        // recordings instead of being silently overridden by the
        // presentation surface's SelfView seed.
        merged.bodyLanguageEnabled =
          persistedPartial.bodyLanguageEnabled === undefined
            ? true
            : Boolean(persistedPartial.bodyLanguageEnabled)
        if (persistedPartial.cameraModeChosen === undefined) {
          merged.cameraModeChosen = merged.cameraMode !== "Off"
        } else {
          merged.cameraModeChosen = Boolean(persistedPartial.cameraModeChosen)
        }
        return {
          ...merged,
          reportingFilters: reanchorRelativeDateRange(
            stringifyLegacyFilterIds(merged.reportingFilters ?? {})
          ),
        }
      },
    }
  )
)
